import { ref } from "vue";

import { type InsightsAction } from "shared/modules/Insights/types";
import { type SelectAllId } from "shared/types";

export type LoadingActions = Partial<Record<InsightsAction, SelectAllId[]>>;

export function useInsightsLoadingActions() {
  const disabledActions = ref<LoadingActions>({});
  const loadingActions = ref<LoadingActions>({});

  function addAction(
    items: LoadingActions,
    action: InsightsAction,
    ids: SelectAllId[]
  ): LoadingActions {
    const actionIds = new Set(items[action] || []);

    ids.forEach((id) => actionIds.add(id));

    return { ...items, [action]: Array.from(actionIds) };
  }

  function removeAction(
    items: LoadingActions,
    action: InsightsAction,
    ids: SelectAllId[]
  ): LoadingActions {
    const actionIds = new Set(items[action] || []);

    ids.forEach((id) => actionIds.delete(id));

    return { ...items, [action]: Array.from(actionIds) };
  }

  function addLoadingAction(action: InsightsAction, ids: SelectAllId[]): void {
    loadingActions.value = addAction(loadingActions.value, action, ids);
  }

  function removeLoadingAction(
    action: InsightsAction,
    ids: SelectAllId[]
  ): void {
    loadingActions.value = removeAction(loadingActions.value, action, ids);
  }

  function addDisabledAction(action: InsightsAction, ids: SelectAllId[]): void {
    disabledActions.value = addAction(disabledActions.value, action, ids);
  }

  function removeDisabledAction(
    action: InsightsAction,
    ids: SelectAllId[]
  ): void {
    disabledActions.value = removeAction(disabledActions.value, action, ids);
  }

  return {
    addDisabledAction,
    addLoadingAction,
    disabledActions,
    loadingActions,
    removeDisabledAction,
    removeLoadingAction,
  };
}

export default useInsightsLoadingActions;
